import { useContext, useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import { getClient } from 'lane-shared/apollo';
import { UserDataContext } from 'lane-shared/contexts';
import { convertToUUID } from 'uuid-encoding';

import { updatePMTaskStepsMutation } from 'graphql-queries';
import type { ClientTask, StepExecution } from 'graphql-query-contracts';

export function usePmTaskStepExecution(taskId: string) {
  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);
  const [currentSteps, setCurrentSteps] = useState<StepExecution[]>([]);

  const syncStepsFromTask = (task?: ClientTask) => {
    setCurrentSteps(task?.stepExecution || []);
  };

  const handleStepResponseChange = (step: StepExecution) => {
    setCurrentSteps(prevSteps => {
      const newSteps = prevSteps.map(cs => (cs.id === step.id ? step : cs));

      updateTaskSteps.current(newSteps);

      return newSteps;
    });
  };

  const updateTaskSteps = useRef(
    debounce(async (steps: StepExecution[]) => {
      if (!taskId || !user?._id) {
        console.error('Invalid taskId or user._id');

        return;
      }

      try {
        await getClient().mutate({
          mutation: updatePMTaskStepsMutation,
          variables: {
            task: {
              id: convertToUUID(taskId),
              updatedBy: convertToUUID(user?._id),
              stepExecution: steps.map(step => ({
                id: step.id,
                name: step.name,
                type: step.type,
                order: step.order,
                taskId: step.taskId,
                isComplete: step.isComplete,
                meterReadingId: step.meterReadingId,
                response: step.response,
                options: {
                  subtype: step.options?.subtype,
                  description: step.options?.description,
                  choices: step.options?.choices,
                  unit: step.options?.unit,
                },
              })),
            },
          },
        });
      } catch (err: any) {
        await window.Alert.alert({
          title: t(
            'web.admin.workOrder.preventiveMaintenance.task.status.update.failure'
          ),
          error: err,
        });
      }
    }, 1000)
  );

  useEffect(() => {
    const debouncedUpdateTaskSteps = updateTaskSteps.current;

    return () => {
      debouncedUpdateTaskSteps.cancel();
    };
  }, []);

  return { currentSteps, handleStepResponseChange, syncStepsFromTask };
}
